import React, { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useLocation, useHistory } from 'react-router-dom'
import TechnicalService from './TechnicalService'
import Download from './Download'
import DashboardHeader from '../common/DashboardHeader/DashboardHeader'
import Breadcrumb from '../common/Breadcrumb'
import WrappedTabs from '../common/WrappedTabs'
import { hasDocumentProtectedPermission, hasEndUserSiteAccess } from '../../utils/permissionValidation'
import { trackEvent, gaActions, gaCategories } from '../../utils/ga4'
import './documents.scss'

function Documents(props) {
  const { t } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const stateModel = location.state && location.state.model
  const canGoBack = location.state && location.state.withBackNavigation
  const model = props.model || stateModel
  const className = props?.className
  const hideBreadcrumb = props.hideBreadcrumb
  const hideFiltering = props.hideFiltering
  const [activeTab, setActiveTab] = useState(null)
  
  const showTSBTab = useMemo(() => hasDocumentProtectedPermission(props.userPermissions), [props.userPermissions])
  
  useEffect(() => {
    if (!hideBreadcrumb) {
      trackEvent({
        action: gaActions.featureClick,
        category: gaCategories.documentsPage,
        label: 'Documents Page: Page View',
      })
    }
  }, [hideBreadcrumb])
  const tabs = useMemo(() => {
    const list = [
      {
        key: 0,
        slug: 'documents',
        title: 'nav.documents',
        gaLabel: 'Downloads',
        content: <Download model={model} />,
      },
    ]
    if (showTSBTab) {
      list.push({
        key: 1,
        slug: 'technical-service-bulletins',
        title: 'technical_service_bulletins',
        gaLabel: 'Technical Service Bulletins',
        content: <TechnicalService />,
      })
    }
    return list
  }, [showTSBTab, model])

  useEffect(() => {
    if (showTSBTab) {
      const state = location.state || {}
      if (state.model) {
        const { model: _removed, ...restState } = state
        history.replace({ pathname: location.pathname, search: location.search }, restState)
      }
    }
  }, [showTSBTab, history, location.pathname, location.search, location.state])

  useEffect(() => {
    if (hasEndUserSiteAccess(props.userPermissions) || hideFiltering) return
    if (!tabs.length) {
      setActiveTab(null)
      return
    }

    const params = new URLSearchParams(location.search)
    const contentParam = params.get('content')
    let selected = tabs[0]
    if (contentParam) {
      const match = tabs.find((t) => t.slug === contentParam)
      if (match) selected = match
    }

    setActiveTab(selected)
    if (params.get('content') !== selected.slug) {
      params.set('content', selected.slug)
      history.replace({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
    }
  }, [location.search, location.pathname, location.state, history, tabs, props.userPermissions, hideFiltering])

  const handleTabSwitch = (tabKey) => {
    const tab = tabs.find((t) => t.key === tabKey)
    if (!tab) return
    trackEvent({
      action: gaActions.featureClick,
      category: gaCategories.documentsPage,
      label: `Documents Page: Tab Switch - ${tab.gaLabel}`,
    })
    setActiveTab(tab)
    const params = new URLSearchParams(location.search)
    params.set('content', tab.slug)
    history.push({ pathname: location.pathname, search: `?${params.toString()}` }, location.state || undefined)
  }

  return (
    <div className={`documents-wrapper ${className}`}>
      {!hideBreadcrumb && <Breadcrumb path={location.pathname} withBackNavigation={canGoBack} />}
      <DashboardHeader headerText={t('nav.documents')} />
      {hideFiltering && <div className="divider" />}
      {!hasEndUserSiteAccess(props.userPermissions) && !hideFiltering ? (
        <>
          <WrappedTabs
            className="main-page-tabs-wrapper"
            tabs={tabs.map((tab) => ({ id: tab.key, name: tab.title }))}
            activeTab={activeTab?.key}
            changeTab={handleTabSwitch}
          />
          {activeTab?.content}
        </>
      ) : (
        <Download model={model} hideFiltering={hideFiltering} />
      )}
    </div>
  )
}

function mapStateToProps({ userPermissions }) {
  return {
    userPermissions,
  }
}

export default connect(mapStateToProps)(Documents)

